import { Component, Inject, Injector, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { FormGroup, Validators } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { AbstractComponent, Task, TasksService, taskTypeLabelKey } from 'lib-trend-core';
import { takeUntil } from 'rxjs';
import { ScheduleMoment } from '../schedule-moment-field/schedule-moment-field.component';

export interface PostponeActivityDialogData {
  task: Task;
}

/**
 * Postpones an open activity (SPEC-2026-10-crm-activities-cadence RF-24 to RF-27): a new date,
 * with or without a time of day, later than now, and a written reason. The proposed moment is the
 * next working day at 09:00 in the company's zone, asked of the API (RF-25) — the browser computes
 * no calendar. The API's refusals (past moment, blank reason, a concurrent change) are shown as
 * they come, already translated.
 */
@Component({
  selector: 'app-postpone-activity-dialog',
  templateUrl: './postpone-activity-dialog.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class PostponeActivityDialogComponent extends AbstractComponent implements OnInit {

  form!: FormGroup;
  saving = false;
  readonly minDate = new Date();

  constructor(
    injector: Injector,
    private readonly dialogRef: MatDialogRef<PostponeActivityDialogComponent, Task | undefined>,
    @Inject(MAT_DIALOG_DATA) public data: PostponeActivityDialogData,
    private readonly tasksService: TasksService,
  ) {
    super(injector);
  }

  ngOnInit(): void {
    this.form = this.formBuilder.group({
      moment: [null, Validators.required],
      reason: ['', Validators.required],
    });

    this.tasksService.nextWorkingMoment()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: ({ dueAt }) => {
          // Only while the user has not typed a moment of their own.
          if (!this.form.get('moment')?.value && dueAt) {
            this.form.patchValue({ moment: <ScheduleMoment>{ dueAt: new Date(dueAt), untimed: false } });
          }
        },
        // No default is not a blocker: the field is still there to be filled by hand (RNF-07).
        error: () => undefined,
      });
  }

  get task(): Task {
    return this.data?.task;
  }

  get taskTitle(): string {
    const name = this.task?.name?.trim();
    const type = this.task?.type;
    return name && name !== type ? name : this.t(taskTypeLabelKey(type));
  }

  get currentFormat(): string {
    return this.task?.untimed === true ? 'DD/MM/YYYY' : 'DD/MM/YYYY HH:mm';
  }

  get showReasonError(): boolean {
    const control = this.form.get('reason');
    return !!control && control.touched && control.invalid;
  }

  confirm(): void {
    if (this.form.invalid || this.saving) {
      this.form.markAllAsTouched();
      return;
    }

    const moment = this.form.value.moment as ScheduleMoment;
    this.saving = true;

    this.tasksService.postpone(this.task._id, {
      dueAt: new Date(moment.dueAt).toISOString(),
      untimed: moment.untimed === true,
      reason: `${this.form.value.reason ?? ''}`.trim(),
    })
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (task) => {
          this.alertService.success(this.t('task.postpone.saved'));
          this.dialogRef.close(task);
        },
        error: (error) => {
          this.saving = false;
          this.alertService.error(error?.error?.message ?? this.t('task.postpone.error'));
        },
      });
  }

  close(): void {
    this.dialogRef.close(undefined);
  }
}
